import { Component, signal, ChangeDetectionStrategy, inject, effect, computed, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { HeaderComponent } from './layout/header/header';
import { FooterComponent } from './layout/footer/footer';
import { EventsService } from '@shared/services/events.service';
import { DOCUMENT } from '@angular/common';
import { filter } from 'rxjs';
import { MaintenanceService } from '@shared/services/maintenance.service';
import { AuthService } from '@shared/services/auth.service';
import { ImpersonationBannerComponent } from './layout/impersonation-banner/impersonation-banner';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent, ImpersonationBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'd-flex flex-column min-vh-100'
  }
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal("Gestiune Evenimente Jidvei");
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly authService = inject(AuthService);

  private readonly currentUrl = signal<string>("");
  private readonly eventsService = inject(EventsService);

  private maintenanceCheckInterval: any;

  protected readonly isStaffPage = computed(() => {
    return this.currentUrl().startsWith('/staff');
  });

  protected readonly isMaintenancePage = computed(() => {
    return this.currentUrl() === '/maintenance';
  });

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      const url = event.urlAfterRedirects || event.url;
      this.currentUrl.set(url);

      // Reset activity color when moving away from event pages
      if (!url.includes('/events/') && !url.includes('/dashboard/events/')) {
        this.eventsService.currentActivityColor.set(null);
      }

      const isAuthPage = url.includes('/auth/');
      if (isAuthPage) {
        this.document.body.classList.add('bg-image');
      } else {
        this.document.body.classList.remove('bg-image');
      }
    });
  }

  ngOnInit() {
    // this.startMaintenanceCheck();
    this.initGTM();
  }

  private initGTM() {
    this.eventsService.getPublicConfig().subscribe({
      next: (config) => {
        const gtmId = config.GOOGLE_TAG_MANAGER_ID;
        if (gtmId) {
          this.injectGTM(gtmId);
        }
      }
    });
  }

  private injectGTM(gtmId: string) {
    const script = this.document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    this.document.head.appendChild(script);

    const noscript = this.document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    this.document.body.insertBefore(noscript, this.document.body.firstChild);
  }

  ngOnDestroy() {
    if (this.maintenanceCheckInterval) {
      clearInterval(this.maintenanceCheckInterval);
    }
  }

  private startMaintenanceCheck() {
    // Check every 30 seconds
    this.maintenanceCheckInterval = setInterval(async () => {
      // Don't apply maintenance redirection for staff members
      if (this.authService.isStaff()) {
        return;
      }

      const wasMaintenance = this.maintenanceService.maintenanceMode();
      const isMaintenance = await this.maintenanceService.checkStatus();

      if (wasMaintenance && !isMaintenance) {
        // Maintenance turned off, hard refresh to get new app version
        window.location.href = '/';
      } else if (!wasMaintenance && isMaintenance) {
        // Maintenance turned on, redirect to maintenance page
        this.router.navigate(['/maintenance']);
      }
    }, 30000);
  }
}
